import { useCallback, useState } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useDebounce } from '@backrs_ui/grow-ui/src/hooks/useDebounce';
import { useSandboxGate } from '@backrs_ui/grow-ui/src/hooks/useSandboxGate';
import {
  useDeleteAssigneeNoteMutation,
  useGetAssigneeNotesQuery,
} from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.api';
import type { AssigneeNote } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.types';
import {
  openCreateNote,
  openEditNote,
} from '@backrs_ui/grow-ui/src/redux/coachNoteEditor/coachNoteEditor';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Snackbar,
  SxProps,
  TextField,
  Theme,
  Typography,
} from '@mui/material';

import { attachmentsToChips, registryEntriesFromBody } from './coachNoteDraft';
import { CoachNoteItem } from './CoachNoteItem';
import { useAgentContext } from './CoachNotesList.agentContext';

const strings = appStrings.modules.coachingDashboard.coachNotes;

type CoachNotesListProps = Readonly<{
  userId: number;
  subjectName: string | null;
}>;

export const CoachNotesList = ({ userId, subjectName }: CoachNotesListProps) => {
  const dispatch = useAppDispatch();
  const gateInSandbox = useSandboxGate();
  const [expandedNoteId, setExpandedNoteId] = useState<number | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebounce(searchValue, 300);
  const trimmedSearch = debouncedSearch.trim();

  const {
    data: notes,
    isLoading,
    isError,
  } = useGetAssigneeNotesQuery({ userId, q: trimmedSearch || undefined });

  // Register the full note set with Lubav, not the search-filtered view.
  const { data: allNotes } = useGetAssigneeNotesQuery({ userId });

  useAgentContext(allNotes ?? []);

  const [deleteNote] = useDeleteAssigneeNoteMutation();

  const handleOpenCreate = useCallback(() => {
    if (gateInSandbox()) return;

    dispatch(openCreateNote({ subjectId: userId, subjectName }));
  }, [dispatch, gateInSandbox, userId, subjectName]);

  const handleOpenEdit = useCallback(
    (note: AssigneeNote) => {
      if (gateInSandbox()) return;

      dispatch(
        openEditNote({
          subjectId: userId,
          subjectName,
          noteId: note.id,
          body: note.body,
          chips: attachmentsToChips(note.attachments),
          registry: registryEntriesFromBody(note.body),
          createdAt: note.created_at,
          lastSavedAt: note.updated_at,
          author: note.author,
        }),
      );
    },
    [dispatch, gateInSandbox, userId, subjectName],
  );

  const handleDelete = useCallback(
    async (note: AssigneeNote) => {
      if (gateInSandbox()) return;

      setPendingDeleteId(note.id);

      try {
        await deleteNote({ userId, noteId: note.id }).unwrap();
      } catch {
        setErrorMessage(strings.deleteError);
      } finally {
        setPendingDeleteId(null);
      }
    },
    [deleteNote, gateInSandbox, userId],
  );

  const handleToggle = useCallback((noteId: number) => {
    setExpandedNoteId((current) => (current === noteId ? null : noteId));
  }, []);

  return (
    <Box sx={containerSx}>
      <Box sx={headerSx}>
        <TextField
          id="coach-notes-search-input"
          placeholder={strings.searchPlaceholder}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          size="small"
          sx={searchInputSx}
          inputProps={{ 'aria-label': strings.searchPlaceholder }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AppIcon
                  iconName="SearchMd"
                  diameter="20px"
                  color="Colors/Foreground/fg-quaternary"
                />
              </InputAdornment>
            ),
          }}
        />
        <Button
          id="coach-notes-create-btn"
          hierarchy="Primary"
          size="sm"
          onClick={handleOpenCreate}
          startIcon={<AppIcon iconName="Plus" diameter="20px" color="currentColor" />}
        >
          {strings.createButton}
        </Button>
      </Box>

      {isLoading && (
        <Box sx={stateSx}>
          <CircularProgress size={24} />
        </Box>
      )}

      {isError && (
        <Alert severity="error" sx={alertSx}>
          {strings.loadError}
        </Alert>
      )}

      {!isLoading && !isError && notes && notes.length === 0 && (
        <Box sx={stateSx}>
          <Typography variant="text-sm-regular" sx={emptyTextSx}>
            {trimmedSearch ? strings.searchEmptyState : strings.emptyState}
          </Typography>
        </Box>
      )}

      {!isLoading && !isError && notes && notes.length > 0 && (
        <Box sx={listSx}>
          {notes.map((note) => (
            <CoachNoteItem
              key={note.id}
              note={note}
              expanded={expandedNoteId === note.id}
              onToggle={handleToggle}
              onEdit={handleOpenEdit}
              onDelete={handleDelete}
              isDeleting={pendingDeleteId === note.id}
            />
          ))}
        </Box>
      )}

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorMessage(null)} severity="error" sx={alertSx}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
  padding: `${backrs.spacing['spacing-xs']} ${backrs.spacing['spacing-md']} ${backrs.spacing['spacing-xs']} ${backrs.spacing['spacing-none']}`,
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
  marginBottom: backrs.spacing['spacing-md'],
});

const searchInputSx: SxProps<Theme> = {
  flex: 1,
  maxWidth: 320,
};

const listSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
};

const stateSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  py: backrs.spacing['spacing-2xl'],
});

const emptyTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/600'],
  textAlign: 'center',
});

const alertSx: SxProps<Theme> = {
  width: '100%',
};
